import { useState, useMemo } from 'react';
import { Grid, GridContainer, Card, CardBody, CardHeader, Button } from '@trussworks/react-uswds';
import { useNavigate } from '@tanstack/react-router';
import Icon from '@mdi/react';
import {
  mdiAccountCircle,
  mdiPlayCircle,
  mdiMap,
  mdiClipboardText,
  mdiPencilBox,
  mdiFileUpload,
  mdiWrench,
  mdiBookOpenPageVariant,
  mdiEmail,
  mdiMapMarker,
  mdiMagnify,
  mdiClockOutline,
  mdiArrowRight,
} from '@mdi/js';

import FeedbackModal from './archive/modals/FeedbackModal';
import { useModal } from '@stores';
import ProgressiveImage from '@src/app-components/progressive-image/ProgressiveImage';
import { supportEmailHref } from '@forms/components/RequestAssistance/helpers';

import faqData from './data/faqData.json';
import videosData from './data/videosData.json';
import articlesData from './data/articlesData.json';

import './support.scss';

// Icon mapping for categories
const iconMap = {
  mdiAccountCircle,
  mdiPlayCircle,
  mdiMap,
  mdiClipboardText,
  mdiPencilBox,
  mdiFileUpload,
  mdiWrench,
};

// Get popular FAQs
const getPopularFaqs = () => {
  const popularIds = faqData.popularQuestions;
  const faqs = [];
  faqData.categories.forEach((category) => {
    category.items.forEach((item) => {
      if (popularIds.includes(item.id)) {
        faqs.push({ ...item, categoryTitle: category.title, categoryId: category.id });
      }
    });
  });
  return faqs.sort((a, b) => popularIds.indexOf(a.id) - popularIds.indexOf(b.id));
};

// Get featured videos
const getFeaturedVideos = () => {
  const featuredIds = videosData.featuredVideos;
  return videosData.videos.filter((video) => featuredIds.includes(video.id));
};

const Support = () => {
  const { openModal } = useModal();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const popularFaqs = useMemo(() => getPopularFaqs(), []);
  const featuredVideos = useMemo(() => getFeaturedVideos(), []);
  //   const popularArticles = useMemo(() => {
  //     return articlesData.popularArticles
  //       .map((slug) => articlesData.articles.find((a) => a.slug === slug))
  //       .filter(Boolean);
  //   }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const value = searchQuery.trim();
    navigate({
      to: '/support/support-faq',
      search: value ? { search: value } : {},
    });
  };

  const navigateTo = (path) => {
    navigate({ to: path });
  };

  const navigateToGuide = (slug) => {
    navigate({
      to: '/support/support-guide-article',
      search: { slug },
    });
  };

  return (
    <div className='support-center'>
      {/* Hero Section */}
      <section className='support-hero'>
        <GridContainer>
          <div className='support-hero-content'>
            <h1>How can we help you today?</h1>
            <p className='support-hero-subtitle'>
              Search our knowledge base or browse categories below to find answers to common questions.
            </p>
            <form onSubmit={handleSearch} className='support-search-form'>
              <div className='support-search-wrapper'>
                <input
                  type='text'
                  className='support-search-input'
                  placeholder='Search for help topics, FAQs, or guides...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label='Search support'
                />
                <Button type='submit' className='support-search-button'>
                  <Icon path={mdiMagnify} size={1} />
                  Search
                </Button>
              </div>
            </form>
            <div className='support-popular-searches'>
              <span>Popular:</span>
              <button onClick={() => navigateTo('/support/support-faq#gis-geodatabase')} className='support-tag-link'>
                GDB Upload
              </button>
              <button onClick={() => navigateToGuide('login-gov-setup')} className='support-tag-link'>
                Account Setup
              </button>
              <button onClick={() => navigateTo('/support/support-faq#managing-requests')} className='support-tag-link'>
                Request Status
              </button>
            </div>
          </div>
        </GridContainer>
      </section>

      {/* Quick Answers - Category Cards */}
      <section className='support-section support-categories'>
        <GridContainer>
          <h2>Quick Answers</h2>
          <p className='section-description'>Browse by category to find answers to frequently asked questions.</p>
          <Grid row gap='md'>
            {faqData.categories.map((category) => (
              <Grid key={category.id} desktop={{ col: 3 }} tablet={{ col: 6 }} mobile={{ col: 12 }}>
                <Card
                  className='support-category-card'
                  onClick={() => navigateTo(`/support/support-faq#${category.id}`)}
                  style={{ cursor: 'pointer', '--category-color': category.color }}
                >
                  <CardHeader>
                    <div className='category-icon-wrapper' style={{ backgroundColor: category.color }}>
                      <Icon path={iconMap[category.icon]} size={1.2} color='#fff' />
                    </div>
                    <h3>{category.title}</h3>
                  </CardHeader>
                  <CardBody>
                    <p className='category-description'>{category.description}</p>
                    <ul className='category-preview-list'>
                      {category.items.slice(0, 3).map((item) => (
                        <li key={item.id}>{item.question.split('?')[0].substring(0, 35)}...</li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              </Grid>
            ))}
            {/* Guides & Tutorials Card */}
            <Grid desktop={{ col: 3 }} tablet={{ col: 6 }} mobile={{ col: 12 }}>
              <Card
                className='support-category-card'
                onClick={() => navigateTo('/support/support-guides')}
                style={{ cursor: 'pointer', '--category-color': '#1b1b1b' }}
              >
                <CardHeader>
                  <div className='category-icon-wrapper' style={{ backgroundColor: '#1b1b1b' }}>
                    <Icon path={mdiBookOpenPageVariant} size={1.2} color='#fff' />
                  </div>
                  <h3>Guides & Tutorials</h3>
                </CardHeader>
                <CardBody>
                  <p className='category-description'>Step-by-step guides and video tutorials</p>
                  <ul className='category-preview-list'>
                    <li>Login.gov Setup</li>
                    <li>First Request Guide</li>
                    <li>GDB Preparation</li>
                  </ul>
                </CardBody>
              </Card>
            </Grid>
          </Grid>
        </GridContainer>
      </section>

      {/* Popular Help Articles */}
      <section className='support-section support-articles'>
        <GridContainer>
          <h2>Popular Help Articles</h2>
          <div className='articles-list'>
            {popularFaqs.map((faq, index) => (
              <button
                key={faq.id}
                className='article-link'
                onClick={() => navigateTo(`/support/support-faq#${faq.categoryId}`)}
              >
                <span className='article-number'>{index + 1}</span>
                <span className='article-title'>{faq.question}</span>
                <span className='article-category'>{faq.categoryTitle}</span>
              </button>
            ))}
          </div>
          <div className='articles-cta'>
            <Button type='button' outline onClick={() => navigateTo('/support/support-faq')}>
              View All FAQs
            </Button>
          </div>
        </GridContainer>
      </section>

      {/* Video Tutorials */}
      <section className='support-section support-videos'>
        <GridContainer>
          <h2>Video Tutorials</h2>
          <p className='section-description'>Watch step-by-step video guides to help you navigate the RRS.</p>
          <Grid row gap='md'>
            {featuredVideos.map((video) => (
              <Grid key={video.id} desktop={{ col: 3 }} tablet={{ col: 6 }} mobile={{ col: 12 }}>
                <Card className='support-video-card'>
                  <div className='video-thumbnail'>
                    <ProgressiveImage
                      alt={`${video.title} video thumbnail`}
                      className='video-image'
                      localPath={`video-stills/${video.image}`}
                      hasAvif
                      hasWebp
                    />
                    <span className='video-duration'>{video.duration}</span>
                  </div>
                  <CardBody>
                    <h4>{video.title}</h4>
                    <p>{video.description.substring(0, 100)}...</p>
                    <a href={video.url} target='_blank' rel='noopener noreferrer' className='video-play-link'>
                      Watch Video
                    </a>
                  </CardBody>
                </Card>
              </Grid>
            ))}
          </Grid>
        </GridContainer>
      </section>

      {/* Step-by-Step Guides */}
      <section className='support-section support-guides-section'>
        <GridContainer>
          <h2>Step-by-Step Guides</h2>
          <p className='section-description'>Detailed walkthroughs for common tasks. Follow along at your own pace.</p>
          <Grid row gap='lg'>
            {articlesData.articles.map((article) => (
              <Grid key={article.slug} desktop={{ col: 4 }} tablet={{ col: 6 }} mobile={{ col: 12 }}>
                <button type='button' className='guide-card' onClick={() => navigateToGuide(article.slug)}>
                  <div className='guide-card-header'>
                    <h3>{article.title}</h3>
                    <span className='guide-read-time'>
                      <Icon path={mdiClockOutline} size={0.7} />
                      {article.readTime} read
                    </span>
                  </div>
                  <p className='guide-summary'>{article.summary}</p>
                  <span className='guide-link'>
                    Read Guide <Icon path={mdiArrowRight} size={0.8} />
                  </span>
                </button>
              </Grid>
            ))}
          </Grid>
        </GridContainer>
      </section>

      {/* Still Need Help? Contact Section */}
      <section className='support-section support-contact'>
        <GridContainer>
          <h2>Still Need Help?</h2>
          <p className='section-description'>
            Can't find what you're looking for? Our support team is here to assist you.
          </p>
          <Grid row gap='lg'>
            <Grid desktop={{ col: 6 }} tablet={{ col: 12 }}>
              <Card className='support-contact-card'>
                <CardHeader>
                  <Icon path={mdiEmail} size={1.5} className='contact-icon' />
                  <h3>Submit a Support Ticket</h3>
                </CardHeader>
                <CardBody>
                  <p>
                    Need assistance with navigating the RRS, encountering technical issues, or require help with account
                    setup? Submit an email inquiry and our support team will respond.
                  </p>
                  <p className='support-hours'>
                    <strong>Support Hours:</strong> 9:00 AM - 9:00 PM EST, Monday - Friday
                  </p>
                  <a
                    href={supportEmailHref}
                    className='usa-button usa-button--outline'
                    role='button'
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    Email RRS Support
                  </a>
                </CardBody>
              </Card>
            </Grid>
            <Grid desktop={{ col: 6 }} tablet={{ col: 12 }}>
              <Card className='support-contact-card'>
                <CardHeader>
                  <Icon path={mdiMapMarker} size={1.5} className='contact-icon' />
                  <h3>Contact Your Local Regulatory Office</h3>
                </CardHeader>
                <CardBody>
                  <p>
                    Looking for help regarding USACE Regulatory Program guidance, policy, or regulations? Contact the
                    USACE district in your area for specialized assistance.
                  </p>
                  <p className='support-note'>
                    <strong>Best for:</strong> Questions about permit requirements, project-specific guidance
                  </p>
                  <Button
                    type='button'
                    outline
                    onClick={() =>
                      window.open('https://regulatory.ops.usace.army.mil/offices/', '_blank', 'noopener,noreferrer')
                    }
                  >
                    Find Your Office
                  </Button>
                </CardBody>
              </Card>
            </Grid>
          </Grid>
        </GridContainer>
      </section>

      {/* Feedback Section */}
      <section className='support-section support-feedback'>
        <GridContainer>
          <div className='feedback-banner'>
            <div className='feedback-content'>
              <h3>Help us improve</h3>
              <p>Have feedback or suggestions for the RRS? We'd love to hear from you.</p>
            </div>
            <Button type='button' outline onClick={() => openModal(FeedbackModal)}>
              Submit Feedback
            </Button>
          </div>
        </GridContainer>
      </section>
    </div>
  );
};

export default Support;
